/**
 * 用户代理检测工具
 * 提供平台和浏览器检测功能
 */

/**
 * 平台类型枚举
 */
export type PlatformType = 'android' | 'ios' | 'pc' | 'server' | 'others';

/**
 * 浏览器类型枚举
 */
export type BrowserType =
  | 'Chrome'
  | 'Safari'
  | 'Firefox'
  | 'Edge'
  | 'Opera'
  | 'IE'
  | 'Others';

/**
 * 检测当前设备平台
 * @returns {PlatformType} 平台类型
 */
export function detectPlatform(): PlatformType {
  // 服务器端渲染环境检查
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'server';
  }

  try {
    // 获取用户代理字符串，移除已弃用的 window.opera
    const ua = navigator.userAgent || navigator.vendor || '';

    // Android 设备检测
    if (/android/i.test(ua)) {
      return 'android';
    }

    // iOS 设备检测（包括 iPad、iPhone、iPod）
    // 移除已弃用的 window.MSStream 检查
    if (/iPad|iPhone|iPod/i.test(ua)) {
      return 'ios';
    }

    // 检测 iOS 13+ 的 iPad（可能显示为桌面 Safari）
    if (/Macintosh/i.test(ua) && 'ontouchend' in document) {
      return 'ios';
    }

    // PC 平台检测
    if (/Windows|Macintosh|Linux/i.test(ua)) {
      return 'pc';
    }

    return 'others';
  } catch (error) {
    // 错误处理：如果检测失败，默认返回 PC
    console.warn('Platform detection failed:', error);
    return 'pc';
  }
}

/**
 * 检测当前浏览器类型
 * @returns {BrowserType} 浏览器类型
 */
export function detectBrowser(): BrowserType {
  // 服务器端渲染环境检查
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'Others';
  }

  try {
    const ua = navigator.userAgent;

    // 检测顺序很重要：从最具体到最通用

    // Edge 浏览器（新版基于 Chromium）
    if (/Edg\//i.test(ua)) {
      return 'Edge';
    }

    // Opera 浏览器
    if (/OPR\//i.test(ua) || /Opera/i.test(ua)) {
      return 'Opera';
    }

    // Chrome 浏览器（排除 Edge 和 Opera）
    if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua) && !/OPR\//i.test(ua)) {
      return 'Chrome';
    }

    // Safari 浏览器（排除 Chrome 内核浏览器）
    if (
      /Safari\//i.test(ua) &&
      !/Chrome\//i.test(ua) &&
      !/Chromium\//i.test(ua)
    ) {
      return 'Safari';
    }

    // Firefox 浏览器
    if (/Firefox\//i.test(ua)) {
      return 'Firefox';
    }

    // Internet Explorer（包括 IE11）
    if (/MSIE|Trident/i.test(ua)) {
      return 'IE';
    }

    return 'Others';
  } catch (error) {
    // 错误处理：如果检测失败，默认返回 Others
    console.warn('Browser detection failed:', error);
    return 'Others';
  }
}
